import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, Save, Bell } from 'lucide-react';
import { automationAPI, agentsAPI, teamsAPI, labelsAPI, complaintTicketsAPI } from '../../services/api';
import { toast } from '../ui/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Card, CardContent } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import AutomationActionParamsEditor from './AutomationActionParamsEditor';
import AutomationFlowEditor from './AutomationFlowEditor';
import CategoryAutocompleteInput from './CategoryAutocompleteInput';
import { tagTree, stripIds, treeHasBranch, makeActionNode, type ActionNode } from './automationActionTree';
import {
  TICKET_EVENTS,
  OPERATORS,
  CONDITION_FIELDS,
  TICKET_CONDITION_FIELDS,
  ACTION_TYPES,
} from './automationConstants';

interface AutomationRuleEditorProps {
  ruleId?: number | null;
  // §3.7: pre-fills the (blank) create form from a starter template instead
  // of an existing saved rule — same shape as what loadRule() would produce,
  // just sourced from the static catalog rather than the API.
  initialTemplate?: {
    name: string;
    description?: string;
    trigger_event: string;
    conditions: any[];
    actions: any[];
  } | null;
  onSaved: (rule: any) => void;
}

const EVENT_TYPES = [
  { value: 'conversation_created', label: 'Conversation Created' },
  { value: 'conversation_updated', label: 'Conversation Updated' },
  { value: 'message_created', label: 'Message Created' },
  { value: 'conversation_opened', label: 'Conversation Opened' },
  { value: 'ticket_created', label: 'Ticket Created' },
  { value: 'ticket_status_changed', label: 'Ticket Status Changed' },
  { value: 'ticket_assigned', label: 'Ticket Assigned' },
  { value: 'ticket_priority_changed', label: 'Ticket Priority Changed' },
  { value: 'sla_at_risk', label: 'Ticket SLA At Risk' },
  { value: 'sla_breached', label: 'Ticket SLA Breached' },
];

/**
 * 2026-08-09: the rule editor, embedded directly in AutomationRulesPage's
 * main content area — extracted out of what used to be AutomationRuleDialog
 * (a modal) so the canvas can be the page's default view instead of
 * something that only appears in a popup. Nothing about the editing logic
 * changed in this extraction, only the outer chrome: no Dialog/DialogContent
 * wrapper, no open/onClose — the page decides what's "selected" and mounts
 * this with a fresh `key` per rule (see AutomationRulesPage.tsx) so internal
 * state resets cleanly between rules without needing an imperative reset.
 */
const AutomationRuleEditor: React.FC<AutomationRuleEditorProps> = ({
  ruleId,
  initialTemplate,
  onSaved,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_event: 'conversation_created',
    enabled: true,
    priority: 0,
    stop_processing: false,
  });

  const [conditions, setConditions] = useState<any[]>([
    { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' },
  ]);

  const [actions, setActions] = useState<ActionNode[]>(
    tagTree([{ action_type: 'assign_agent', action_parameters: {}, execution_order: 0 }])
  );
  const [viewMode, setViewMode] = useState<'simple' | 'flow'>('simple');

  const [agents, setAgents] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [labels, setLabels] = useState<any[]>([]);
  const [categories, setCategories] = useState<{ id: string; label: string }[]>([]);
  const [templates, setTemplates] = useState<{ id: number; name: string; language?: string }[]>([]);
  const [channels, setChannels] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void loadResources();
    if (ruleId) void loadRule();
    else if (initialTemplate) applyTemplate(initialTemplate);
    else resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyTemplate = (template: NonNullable<AutomationRuleEditorProps['initialTemplate']>) => {
    setFormData({
      name: template.name,
      description: template.description || '',
      trigger_event: template.trigger_event,
      enabled: true,
      priority: 0,
      stop_processing: false,
    });
    setConditions(
      template.conditions.length > 0
        ? template.conditions
        : [{ field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' }]
    );
    const tagged = tagTree(
      template.actions.length > 0
        ? template.actions
        : [{ action_type: 'assign_agent', action_parameters: {}, execution_order: 0 }]
    );
    setActions(tagged);
    setViewMode(treeHasBranch(tagged) ? 'flow' : 'simple');
  };

  const loadResources = async () => {
    try {
      const [agentsData, teamsData, labelsData, ticketConfig, ticketConfigOptions] = await Promise.all([
        agentsAPI.getAgents().catch(() => ({ agents: [] })),
        teamsAPI.getTeams().catch(() => ({ teams: [] })),
        labelsAPI.getLabels().catch(() => ({ labels: [] })),
        complaintTicketsAPI.getConfig().catch(() => null),
        complaintTicketsAPI.getConfigOptions().catch(() => null),
      ]);
      setAgents(agentsData.agents || agentsData || []);
      setTeams(teamsData.teams || teamsData || []);
      setLabels(labelsData.labels || labelsData || []);
      const rawCategories = (ticketConfig?.config?.categories || []) as any[];
      setCategories(rawCategories.map((c) => ({ id: c.id, label: c.labels?.en || c.id })));
      setTemplates(ticketConfigOptions?.templates || []);
      setChannels(ticketConfigOptions?.channels || []);
    } catch (err) {
      console.error('Failed to load resources:', err);
    }
  };

  const loadRule = async () => {
    try {
      setLoading(true);
      const rule = await automationAPI.getAutomationRule(ruleId!);
      setFormData({
        name: rule.name,
        description: rule.description || '',
        trigger_event: rule.trigger_event,
        enabled: rule.enabled,
        priority: rule.priority ?? 0,
        stop_processing: rule.stop_processing ?? false,
      });
      if (rule.conditions && rule.conditions.length > 0) setConditions(rule.conditions);
      if (rule.actions && rule.actions.length > 0) {
        const tagged = tagTree(rule.actions);
        setActions(tagged);
        setViewMode(treeHasBranch(tagged) ? 'flow' : 'simple');
      }
    } catch (err) {
      console.error('Failed to load rule:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger_event: 'conversation_created',
      enabled: true,
      priority: 0,
      stop_processing: false,
    });
    setConditions([
      { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' },
    ]);
    setActions(tagTree([{ action_type: 'assign_agent', action_parameters: {}, execution_order: 0 }]));
    setViewMode('simple');
  };

  const handleAddCondition = () => {
    setConditions([
      ...conditions,
      { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' },
    ]);
  };

  const handleRemoveCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const handleConditionChange = (index: number, field: string, value: any) => {
    const next = [...conditions];
    next[index] = { ...next[index], [field]: value };
    setConditions(next);
  };

  const handleAddAction = () => {
    setActions([
      ...actions,
      { ...makeActionNode(), execution_order: actions.length },
    ]);
  };

  // 2026-08-10: "Add action" defaults to Assign to Agent — getting to the
  // notify_agent/notify_manager Template/Channel override (already built,
  // see AutomationActionParamsEditor.tsx) took add action -> open the type
  // dropdown -> find and pick Notify Assigned Agent, three steps before the
  // fields even appeared. This jumps straight there in one click.
  const handleAddNotifyAction = () => {
    setActions([
      ...actions,
      { ...makeActionNode('notify_agent'), execution_order: actions.length },
    ]);
  };

  const handleRemoveAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const handleActionChange = (index: number, field: string, value: any) => {
    const next = [...actions];
    next[index] = { ...next[index], [field]: value };
    setActions(next);
  };

  const handleActionParameterChange = (index: number, paramKey: string, paramValue: any) => {
    const next = [...actions];
    next[index] = {
      ...next[index],
      action_parameters: {
        ...next[index].action_parameters,
        [paramKey]: paramValue,
      },
    };
    setActions(next);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const payload = {
        ...formData,
        conditions: conditions.map((c) => ({
          ...c,
          condition_group: c.condition_group || 'default',
        })),
        actions: stripIds(actions).map((a, i) => ({
          ...a,
          execution_order: a.execution_order !== undefined ? a.execution_order : i,
        })),
      };

      const saved = ruleId
        ? await automationAPI.updateAutomationRule(ruleId, payload)
        : await automationAPI.createAutomationRule(payload);

      toast({ title: ruleId ? 'Rule updated' : 'Rule created' });
      onSaved(saved);
    } catch (err: any) {
      console.error('Failed to save rule:', err);
      toast({
        title: 'Could not save rule',
        description: err.response?.data?.detail || err.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderActionParameters = (action: any, index: number) => (
    <div className="mt-4">
      <AutomationActionParamsEditor
        actionType={action.action_type}
        params={action.action_parameters || {}}
        onChange={(key, value) => handleActionParameterChange(index, key, value)}
        isTicketEvent={TICKET_EVENTS.has(formData.trigger_event)}
        agents={agents}
        teams={teams}
        labels={labels}
        templates={templates}
        channels={channels}
        idPrefix={`action-${index}`}
      />
    </div>
  );

  const hasBranch = treeHasBranch(actions);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{ruleId ? 'Edit rule' : 'Create rule'}</h2>
          <p className="text-sm text-muted-foreground">
            When the trigger fires and conditions match, the actions below run in order.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={saving || !formData.name || conditions.length === 0 || actions.length === 0}
          className="gap-2 shrink-0"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'Saving…' : ruleId ? 'Update rule' : 'Create rule'}
        </Button>
      </div>

      <Separator />

      {/* Rule info */}
      <div className="space-y-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Rule information
        </p>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-3">
            <Label htmlFor="rule-name">Rule name</Label>
            <Input
              id="rule-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. VIP auto-assign"
              required
            />
          </div>
          <div className="space-y-3">
            <Label>Trigger event</Label>
            <Select
              value={formData.trigger_event}
              onValueChange={(v) => setFormData({ ...formData, trigger_event: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((event) => (
                  <SelectItem key={event.value} value={event.value}>
                    {event.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-3">
          <Label htmlFor="rule-desc">Description</Label>
          <Textarea
            id="rule-desc"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={2}
            placeholder="Optional notes for your team"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
            <div>
              <p className="text-sm font-medium">Enable rule</p>
              <p className="text-xs text-muted-foreground">Disabled rules never run</p>
            </div>
            <Switch
              checked={formData.enabled}
              onCheckedChange={(v) => setFormData({ ...formData, enabled: v })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rule-priority">Priority</Label>
            <Input
              id="rule-priority"
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: Number(e.target.value) || 0 })}
            />
            <p className="text-xs text-muted-foreground">
              Higher runs first when multiple rules match the same event.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
            <div>
              <p className="text-sm font-medium">Stop processing</p>
              <p className="text-xs text-muted-foreground">
                Skip lower-priority rules once this one runs
              </p>
            </div>
            <Switch
              checked={formData.stop_processing}
              onCheckedChange={(v) => setFormData({ ...formData, stop_processing: v })}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Conditions — only in List mode; Flow mode edits the same
          `conditions` state via a real node in the canvas below
          (AutomationFlowNodes.tsx's ConditionFlowNode). */}
      {viewMode === 'simple' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Conditions
            </p>
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1" onClick={handleAddCondition}>
              <Plus className="h-3.5 w-3.5" />
              Add condition
            </Button>
          </div>
          {conditions.map((condition, index) => (
            <Card key={index} className="border-border/60 bg-muted/10 shadow-none">
              <CardContent className="space-y-4 p-4 pt-4">
                {index > 0 && (
                  <Select
                    value={condition.group_operator || 'AND'}
                    onValueChange={(v) => handleConditionChange(index, 'group_operator', v)}
                  >
                    <SelectTrigger className="h-8 w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AND">AND</SelectItem>
                      <SelectItem value="OR">OR</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <div className="grid gap-4 sm:grid-cols-12 sm:items-end">
                  <div className="space-y-2 sm:col-span-4">
                    <Label className="text-xs">Field</Label>
                    <Select
                      value={condition.field_name}
                      onValueChange={(v) => handleConditionChange(index, 'field_name', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(TICKET_EVENTS.has(formData.trigger_event)
                          ? TICKET_CONDITION_FIELDS
                          : CONDITION_FIELDS
                        ).map((field) => (
                          <SelectItem key={field.value} value={field.value}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 sm:col-span-4">
                    <Label className="text-xs">Operator</Label>
                    <Select
                      value={condition.operator}
                      onValueChange={(v) => handleConditionChange(index, 'operator', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPERATORS.map((op) => (
                          <SelectItem key={op.value} value={op.value}>
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 sm:col-span-3">
                    <Label className="text-xs">Value</Label>
                    {condition.field_name === 'category_id' ? (
                      <CategoryAutocompleteInput
                        value={condition.expected_value || ''}
                        onChange={(v) => handleConditionChange(index, 'expected_value', v)}
                        categories={categories}
                      />
                    ) : (
                      <Input
                        value={condition.expected_value || ''}
                        onChange={(e) =>
                          handleConditionChange(index, 'expected_value', e.target.value)
                        }
                        placeholder="Compare to…"
                      />
                    )}
                  </div>
                  <div className="flex sm:col-span-1 sm:justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveCondition(index)}
                      disabled={conditions.length === 1}
                      aria-label="Remove condition"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Separator />

      {/* Actions (List mode) / Conditions + Actions (Flow mode) */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {viewMode === 'flow' ? 'Conditions & Actions' : 'Actions'}
          </p>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-md border border-border/60 p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setViewMode('simple')}
                disabled={hasBranch}
                title={hasBranch ? 'This rule has a branch — edit it in Flow view' : undefined}
                className={cn(
                  'rounded px-2 py-1 transition-colors',
                  viewMode === 'simple' ? 'bg-muted font-medium' : 'text-muted-foreground',
                  hasBranch && 'cursor-not-allowed opacity-40',
                )}
              >
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('flow')}
                className={cn(
                  'rounded px-2 py-1 transition-colors',
                  viewMode === 'flow' ? 'bg-muted font-medium' : 'text-muted-foreground',
                )}
              >
                Flow
              </button>
            </div>
            {viewMode === 'simple' && (
              <>
                <Button type="button" variant="outline" size="sm" className="h-8 gap-1" onClick={handleAddAction}>
                  <Plus className="h-3.5 w-3.5" />
                  Add action
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1"
                  onClick={handleAddNotifyAction}
                  title="Adds a Notify Assigned Agent action, with its Template/Channel override ready to set"
                >
                  <Bell className="h-3.5 w-3.5" />
                  Add notification
                </Button>
              </>
            )}
          </div>
        </div>

        {viewMode === 'flow' ? (
          <AutomationFlowEditor
            triggerEvent={formData.trigger_event}
            conditions={conditions}
            onConditionsChange={setConditions}
            actions={actions}
            onChange={setActions}
            isTicketEvent={TICKET_EVENTS.has(formData.trigger_event)}
            agents={agents}
            teams={teams}
            labels={labels}
            categories={categories}
            templates={templates}
            channels={channels}
          />
        ) : (
          actions.map((action, index) => (
            <Card key={action._id} className="border-border/60 bg-muted/10 shadow-none">
              <CardContent className="p-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1 space-y-3">
                    <Label className="text-xs">Action type</Label>
                    <Select
                      value={action.action_type}
                      onValueChange={(v) => handleActionChange(index, 'action_type', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACTION_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {renderActionParameters(action, index)}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive sm:mt-6"
                    onClick={() => handleRemoveAction(index)}
                    disabled={actions.length === 1}
                    aria-label="Remove action"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AutomationRuleEditor;
